import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import styles from "./EditAddOnSetPopup.module.scss";

const EditAddOnSetPopup = ({
  isOpen,
  onClose,
  addOnSet,
  onUpdate,
  isLoading = false,
}) => {
  const [requiredAddOns, setRequiredAddOns] = useState("None");
  const [maximumAddOns, setMaximumAddOns] = useState("None");
  const [addOns, setAddOns] = useState([]);
  const [hideFromCustomer, setHideFromCustomer] = useState(false);
  const [showRequiredDropdown, setShowRequiredDropdown] = useState(false);
  const [showMaximumDropdown, setShowMaximumDropdown] = useState(false);

  // Auto-adjust maximumAddOns when requiredAddOns changes
  useEffect(() => {
    if (requiredAddOns !== "None" && maximumAddOns !== "None") {
      const requiredNum = parseInt(requiredAddOns, 10);
      const maximumNum = parseInt(maximumAddOns, 10);
      if (requiredNum > maximumNum) {
        setMaximumAddOns(requiredAddOns);
      }
    }
  }, [requiredAddOns, maximumAddOns]);
  // Generate options for dropdowns (pure function, no side effects)
  const generateOptions = useCallback((type = "required") => {
    const options = ["None"];
    const maxLength = addOns?.length || 10;

    if (type === "maximum") {
      // For maximum: start from the greater of requiredAddOns or preSelectedCount
      const requiredNum = requiredAddOns === "None" ? 0 : parseInt(requiredAddOns, 10);
      const preSelectedCount = addOns.filter((addon) => addon.preSelected).length;
      const startFrom = Math.max(requiredNum, preSelectedCount, 1);
      
      for (let i = startFrom; i <= maxLength; i++) {
        options.push(i.toString());
      }
      return options;
    }

    for (let i = 1; i <= maxLength; i++) {
      options.push(i.toString());
    }
    return options;
  }, [addOns, requiredAddOns]);

  useEffect(() => {
    if (addOnSet) {
      const requiredValue = addOnSet.requiredAddOns;
      const maximumValue = addOnSet.maximumAddOns;

      // Treat 0 or falsy values as "None"
      const newRequiredAddOns = requiredValue && requiredValue !== 0 ? requiredValue.toString() : "None";
      const newMaximumAddOns = maximumValue && maximumValue !== 0 ? maximumValue.toString() : "None";

      const maximumNum = newMaximumAddOns === "None" ? Infinity : parseInt(newMaximumAddOns, 10);
      const preSelectedCount = addOnSet.addOns?.filter((addon) => addon.preSelected).length || 0;

      setRequiredAddOns(newRequiredAddOns);
      setMaximumAddOns(newMaximumAddOns);
      setHideFromCustomer(addOnSet.hideAddons || addOnSet.hideFromCustomer || false);

      // If maximumAddOns === preSelectedCount, disable unchecked; otherwise enable all
      const isMaxReached = preSelectedCount >= maximumNum;

      setAddOns(
        addOnSet.addOns?.map((addon) => ({
          ...addon,
          preSelected: addOnSet.preSelected?.includes(addon._id) || false,
          // Checked items are never disabled; unchecked items disabled only if max reached
          isDisabled: addon.preSelected ? false : isMaxReached,
        })) || []
      );
    }
  }, [addOnSet]);

  // Recalculate disabled states based on current selections
  const recalculateDisabledStates = useCallback((addOnsList) => {
    const maximumNum = maximumAddOns === "None" ? Infinity : parseInt(maximumAddOns, 10);
    const preSelectedCount = addOnsList.filter((addon) => addon.preSelected).length;

    // If maximumAddOns === preSelectedCount, disable unchecked; otherwise enable all
    const isMaxReached = preSelectedCount >= maximumNum;

    return addOnsList.map((addon) => ({
      ...addon,
      // Checked items are never disabled; unchecked items disabled only if max reached
      isDisabled: addon.preSelected ? false : isMaxReached,
    }));
  }, [maximumAddOns]);

  // Recalculate disabled states when maximumAddOns changes
  useEffect(() => {
    setAddOns((prevAddOns) => {
      if (prevAddOns.length === 0) return prevAddOns;
      return recalculateDisabledStates(prevAddOns);
    });
  }, [maximumAddOns, recalculateDisabledStates]);

  const handlePreSelectedChange = (index) => {
    setAddOns((prevAddOns) => {
      // Toggle the preSelected state
      const updatedAddOns = prevAddOns.map((addon, i) =>
        i === index ? { ...addon, preSelected: !addon.preSelected } : addon
      );
      // Recalculate disabled states
      return recalculateDisabledStates(updatedAddOns);
    });
  };

  const handleUpdate = () => {
    const updatedData = {
      ...addOnSet
      ,requiredAddOns: requiredAddOns === "None" ? 0 : parseInt(requiredAddOns, 10),
      maximumAddOns: maximumAddOns === "None" ? 0 : parseInt(maximumAddOns, 10),
      hideAddons: hideFromCustomer,
      preSelected: addOns.filter((addon) => addon.preSelected).map((addon) => addon._id),
    };
    onUpdate(updatedData);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  const handleSelectOption = (type, value) => {
    if (type === "required") {
      setRequiredAddOns(value);
      setShowRequiredDropdown(false);
    } else {
      // Ensure maximumAddOns is always >= preSelectedCount
      const preSelectedCount = addOns.filter((addon) => addon.preSelected).length;
      const newMaximum = value === "None" ? Infinity : parseInt(value, 10);
      
      if (newMaximum < preSelectedCount) {
        // Set to preSelectedCount if trying to set below it
        setMaximumAddOns(preSelectedCount.toString());
      } else {
        setMaximumAddOns(value);
      }
      setShowMaximumDropdown(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="popup-title"
      tabIndex={-1}
    >
      <div className={styles.popup}>
        {/* Header */}
        <div className={styles.header}>
          <h3 id="popup-title" className={styles.title}>
            Edit {addOnSet?.name || "Set"}
          </h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close popup"
            tabIndex={0}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Dotted Divider */}
        <div className={styles.dottedDivider} />

        {/* Content */}
        <div className={styles.content}>
          {/* Required Add-Ons Dropdown */}
          <div className={styles.dropdownContainer}>
            <button
              type="button"
              className={styles.dropdownButton}
              onClick={() => {
                setShowRequiredDropdown(!showRequiredDropdown);
                setShowMaximumDropdown(false);
              }}
              aria-expanded={showRequiredDropdown}
              aria-haspopup="listbox"
              tabIndex={0}
            >
              <span>Required Add-Ons: {requiredAddOns}</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`${styles.chevron} ${showRequiredDropdown ? styles.rotated : ""}`}
              />
            </button>
            {showRequiredDropdown && (
              <ul className={styles.dropdownList} role="listbox">
                {generateOptions().map((option) => (
                  <li
                    key={`required-${option}`}
                    className={`${styles.dropdownItem} ${requiredAddOns === option ? styles.selected : ""}`}
                    onClick={() => handleSelectOption("required", option)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleSelectOption("required", option);
                      }
                    }}
                    role="option"
                    aria-selected={requiredAddOns === option}
                    tabIndex={0}
                  >
                    {option}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Maximum Add-Ons Dropdown */}
          <div className={styles.dropdownContainer}>
            <button
              type="button"
              className={styles.dropdownButton}
              onClick={() => {
                setShowMaximumDropdown(!showMaximumDropdown);
                setShowRequiredDropdown(false);
              }}
              aria-expanded={showMaximumDropdown}
              aria-haspopup="listbox"
              tabIndex={0}
            >
              <span>Maximum Add-Ons: {maximumAddOns}</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`${styles.chevron} ${showMaximumDropdown ? styles.rotated : ""}`}
              />
            </button>
            {showMaximumDropdown && (
              <ul className={styles.dropdownList} role="listbox">
                {generateOptions("maximum").map((option) => (
                  <li
                    key={`maximum-${option}`}
                    className={`${styles.dropdownItem} ${maximumAddOns === option ? styles.selected : ""}`}
                    onClick={() => handleSelectOption("maximum", option)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleSelectOption("maximum", option);
                      }
                    }}
                    role="option"
                    aria-selected={maximumAddOns === option}
                    tabIndex={0}
                  >
                    {option}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Add-Ons List */}
          <div className={styles.addOnsList}>
            {addOns.map((addon, index) => (
              <div key={addon._id || index} className={styles.addOnItem}>
                <div className={styles.addOnInfo}>
                  <span className={styles.addOnName}>{addon.name}</span>
                  <span className={styles.addOnPrice}>
                    ${addon.price?.toFixed(2) || "0.00"}
                  </span>
                </div>
                <div className={styles.preSelectedContainer}>
                  <label className={`${styles.checkboxLabel} ${addon.isDisabled ? styles.checkboxLabelDisabled : ""}`}>
                    <input
                      type="checkbox"
                      checked={addon.preSelected}
                      disabled={addon.isDisabled}
                      onChange={() => handlePreSelectedChange(index)}
                      className={styles.checkbox}
                      aria-label={`Pre-select ${addon.name}`}
                    />
                    <span className={`${styles.checkboxCustom} ${addon.isDisabled ? styles.checkboxDisabled : ""}`} />
                    <span className={styles.checkboxText}>Pre-Selected</span>
                  </label>
                </div>
              </div>
            ))}
          </div>

          {/* Hide from Customer Checkbox */}
          <div className={styles.hideFromCustomerContainer}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={hideFromCustomer}
                onChange={() => setHideFromCustomer(!hideFromCustomer)}
                className={styles.checkbox}
                aria-label="Hide Add-Ons from customer"
              />
              <span className={styles.checkboxCustom} />
              <span className={styles.hideFromCustomerText}>
                Hide Add-Ons from customer
              </span>
            </label>
          </div>
        </div>

        {/* Update Button */}
        <button
          type="button"
          className={styles.updateButton}
          onClick={handleUpdate}
          disabled={isLoading}
          tabIndex={0}
        >
          {isLoading ? "Updating..." : "Update"}
        </button>
      </div>
    </div>
  );
};

EditAddOnSetPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  addOnSet: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    requiredAddOns: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maximumAddOns: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    hideFromCustomer: PropTypes.bool,
    addOns: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        name: PropTypes.string,
        price: PropTypes.number,
        preSelected: PropTypes.bool,
      })
    ),
  }),
  onUpdate: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

EditAddOnSetPopup.defaultProps = {
  addOnSet: null,
  isLoading: false,
};

export default React.memo(EditAddOnSetPopup);

